/**
 * Organizations whose onboarding was applied in this session.
 *
 * `POST /onboarding/apply` commits in a single transaction and only resolves
 * after it does, so a successful response IS the completion — there is nothing
 * left to confirm. The success step's CTA is only reachable behind that
 * response, which makes "the user pressed it" a deterministic signal.
 *
 * The gate in app/(app)/_layout.tsx used to re-derive the same fact from the
 * cached GET /onboarding/state, which put a routing decision at the mercy of
 * cache coherence: the entry is keyed by UI language, restored from
 * AsyncStorage across reloads, and held fresh for five minutes. Any variant
 * still holding `in_progress` sent a finished user back into the wizard.
 *
 * Deliberately in-memory and synchronous: it is read during render, so an async
 * store would flicker through "unknown" and redirect before it resolved. It only
 * needs to cover the window between applying and the next load — after a reload
 * the server answers, and that answer is authoritative.
 */
const appliedOrganizationIds = new Set<string>();

export function markOnboardingApplied(organizationId: string): void {
  if (organizationId) appliedOrganizationIds.add(organizationId);
}

export function hasAppliedOnboarding(organizationId: string | undefined): boolean {
  return !!organizationId && appliedOrganizationIds.has(organizationId);
}

/** Test seam. Never call this from app code — applying is not reversible. */
export function resetAppliedOnboarding(): void {
  appliedOrganizationIds.clear();
}
